/**
 * AppsPage — GAP-6: Apps / Workspaces / Sites enterprise bundle distribution.
 *
 * Two views in one page:
 *   - Browse: published "Apps" the user is in the audience for (read-only)
 *   - Manage (admins): publish drafts, set audience, add/remove content
 *
 * An "App" is just a workspace flipped into `status: published` with a
 * frozen manifest. End-users see only published Apps; admins see drafts
 * + published with edit controls.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import { apiFetch, pickArray } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect, MultiComboInput } from '@/components/ui/combobox';
import { toast } from 'sonner';
import {
  LayoutGrid, Loader2, Plus, Send, Users, BarChart3, FileText, GitBranch,
  Lock, X, Edit3, Eye, Download, Upload, Trash2, AlertTriangle, Layers, Rocket,
} from 'lucide-react';
import { useTerms, getTerm } from '@/lib/branding';

const API = process.env.REACT_APP_BACKEND_URL;
const KIND_ICON = { dashboard: BarChart3, report: FileText, model: GitBranch, visual: Layers };

function AppCard({ ws, isAdmin, onOpen, onManage, onExport, onDelete, onPromote }) {
  const draft = ws.status !== 'published';
  return (
    <div className="glass-card rounded-2xl p-5 hover:border-primary/30 transition-all group" data-testid={`app-card-${ws.workspace_id}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="p-2 rounded-xl bg-primary/10 border border-primary/20">
          <LayoutGrid className="w-5 h-5 text-primary" />
        </div>
        <div className="flex gap-1.5">
          {draft ? (
            <Badge className="text-[9px] bg-amber-500/15 text-amber-400 border-amber-500/20">Draft</Badge>
          ) : (
            <Badge className="text-[9px] bg-emerald-500/15 text-emerald-400 border-emerald-500/20">v{ws.published_version}</Badge>
          )}
          {(ws.audience?.users?.length || ws.audience?.roles?.length) ? (
            <Badge variant="outline" className="text-[9px] border-border/30"><Lock className="w-2.5 h-2.5 mr-0.5" /> Restricted</Badge>
          ) : null}
        </div>
      </div>
      <h3 className="text-sm font-heading font-semibold text-foreground mb-1">{ws.name}</h3>
      <p className="text-xs text-muted-foreground line-clamp-2 mb-3">{ws.description || 'No description'}</p>
      <div className="flex items-center gap-3 text-[10px] text-muted-foreground mb-3">
        <span>{ws.content_count ?? (ws.content?.length || 0)} asset(s)</span>
        {ws.published_at && <span>Published {new Date(ws.published_at).toLocaleDateString()}</span>}
      </div>
      <div className="flex gap-1.5">
        <Button onClick={() => onOpen(ws)} size="sm" className="flex-1 rounded-lg h-7 text-xs" data-testid={`open-app-${ws.workspace_id}`}>
          <Eye className="w-3 h-3 mr-1" /> Open
        </Button>
        {isAdmin && (
          <>
            <Button onClick={() => onManage(ws)} size="sm" variant="outline" className="rounded-lg h-7 text-xs px-2" title="Manage" data-testid={`manage-app-${ws.workspace_id}`}>
              <Edit3 className="w-3 h-3" />
            </Button>
            <Button onClick={() => onExport(ws)} size="sm" variant="outline" className="rounded-lg h-7 text-xs px-2" title="Export App" data-testid={`export-app-${ws.workspace_id}`}>
              <Download className="w-3 h-3" />
            </Button>
            <Button onClick={() => onPromote(ws)} size="sm" variant="outline" className="rounded-lg h-7 text-xs px-2 text-emerald-400 hover:bg-emerald-500/10 border-emerald-500/30" title="Promote App (Dev → Test → Prod)" data-testid={`promote-app-${ws.workspace_id}`}>
              <Rocket className="w-3 h-3" />
            </Button>
            <Button onClick={() => onDelete(ws)} size="sm" variant="outline" className="rounded-lg h-7 text-xs px-2 text-muted-foreground hover:text-destructive hover:border-destructive/40" title="Delete App" data-testid={`delete-app-${ws.workspace_id}`}>
              <Trash2 className="w-3 h-3" />
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

function DeleteAppDialog({ ws, onClose, onConfirmed }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setTyped(''); }, [ws]);
  if (!ws) return null;
  const match = typed.trim() === (ws.name || '').trim();

  const doDelete = async () => {
    if (!match) return;
    setBusy(true);
    try {
      const { ok, data } = await apiFetch(`${API}/api/workspaces/${ws.workspace_id}`, { method: 'DELETE' });
      if (!ok) throw new Error(data?.detail || 'Failed to delete');
      toast.success(`Deleted "${ws.name}"`);
      onConfirmed?.();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!ws} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="delete-app-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="w-4 h-4" /> Delete {getTerm('apps', 'singular')}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <p className="text-xs text-muted-foreground">
            This permanently removes the App <span className="font-semibold text-foreground">{ws.name}</span>.
            The underlying dashboards, reports, and models are <span className="font-semibold">not</span> deleted.
            This action cannot be undone.
          </p>
          <div>
            <label className="text-[10px] text-muted-foreground">Type the app name <span className="font-mono text-foreground">{ws.name}</span> to confirm</label>
            <Input
              autoFocus
              value={typed}
              onChange={e => setTyped(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && match && !busy) doDelete(); }}
              placeholder={ws.name}
              className="h-8 text-xs rounded-lg bg-muted/30 border-border/30 mt-1"
              data-testid="delete-app-name-input"
            />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-lg" data-testid="delete-app-cancel">Cancel</Button>
          <Button
            onClick={doDelete}
            disabled={!match || busy}
            className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90"
            data-testid="delete-app-confirm"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Trash2 className="w-3.5 h-3.5 mr-1" />}
            Delete {getTerm('apps', 'singular')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AppPromoteDialog({ ws, onClose }) {
  const [busy, setBusy] = useState(false);
  if (!ws) return null;
  const doPromote = async (toStage) => {
    setBusy(true);
    try {
      const { ok, data } = await apiFetch(`${API}/api/workspaces/${ws.workspace_id}/promote`, { method: 'POST', body: JSON.stringify({ to_stage: toStage }) });
      if (!ok) throw new Error(data?.detail || 'Promotion failed');
      toast.success(`Promoted ${data.promoted} insight(s) to ${toStage === 'test' ? 'Test' : 'Production'}${data.skipped ? ` · ${data.skipped} skipped (not yet in ${toStage === 'test' ? 'Dev' : 'Test'})` : ''}`);
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!ws} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-md" data-testid="app-promote-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Rocket className="w-4 h-4 text-emerald-400" /> Promote {getTerm('apps', 'singular')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 mt-1">
          <p className="text-xs text-muted-foreground">
            Promote every report &amp; dashboard in <span className="font-semibold text-foreground">{ws.name}</span> to the next stage at once.
            Reports not yet in the source stage are skipped. Viewers always see the Production version.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Button onClick={() => doPromote('test')} disabled={busy} variant="outline" className="rounded-lg text-xs border-sky-500/40 text-sky-400 hover:bg-sky-500/10" data-testid="app-promote-test">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Rocket className="w-3.5 h-3.5 mr-1" />} Dev → Test
            </Button>
            <Button onClick={() => doPromote('production')} disabled={busy} className="rounded-lg text-xs bg-emerald-600 hover:bg-emerald-600/90" data-testid="app-promote-prod">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Rocket className="w-3.5 h-3.5 mr-1" />} Test → Prod
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function ManageDialog({ ws, allAssets, sysUsers, sysRoles, onClose, onChanged }) {
  const [audienceUsers, setAudienceUsers] = useState((ws?.audience?.users) || []);
  const [audienceRoles, setAudienceRoles] = useState((ws?.audience?.roles) || []);
  const [content, setContent] = useState(ws?.content || []);
  const [pickKind, setPickKind] = useState('dashboard');
  const [pickId, setPickId] = useState('');
  const [busy, setBusy] = useState(false);
  if (!ws) return null;

  const csrf = () => decodeURIComponent((document.cookie.match(/(?:^|; )csrf_token=([^;]+)/) || [])[1] || '');
  const headers = () => ({ 'Content-Type': 'application/json', 'X-CSRF-Token': csrf() });

  const listKeyFor = (kind) => (kind === 'model' ? 'models' : kind === 'dashboard' ? 'dashboards' : kind === 'visual' ? 'visuals' : 'reports');
  const nameFor = (kind, id) => (allAssets?.[listKeyFor(kind)] || []).find(a => a.id === id)?.name || id;
  const userOptions = (sysUsers || []).map(u => ({ value: u.user_id || u.id || u.email, label: u.name || u.email || u.user_id, sub: u.email && (u.name || u.user_id) ? u.email : '' }));
  const roleOptions = (sysRoles || []).map(r => ({ value: r.role_name || r, label: r.role_name || r }));

  const saveAudience = async () => {
    setBusy(true);
    try {
      const body = {
        users: (audienceUsers || []).map(s => String(s).trim()).filter(Boolean),
        roles: (audienceRoles || []).map(s => String(s).trim()).filter(Boolean),
      };
      const { ok, data } = await apiFetch(`${API}/api/workspaces/${ws.workspace_id}/audience`, {
        method: 'PUT', headers: headers(), body: JSON.stringify(body),
      });
      if (!ok) throw new Error(data?.detail || 'Failed to save audience');
      toast.success('Audience updated');
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const addAsset = async () => {
    if (!pickId) return;
    setBusy(true);
    try {
      const { ok, data } = await apiFetch(`${API}/api/workspaces/${ws.workspace_id}/content`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ kind: pickKind, id: pickId }),
      });
      if (!ok) throw new Error(data?.detail || 'Failed to add asset');
      setContent(data.content || []);
      setPickId('');
      toast.success('Added to App');
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const removeAsset = async (kind, id) => {
    setBusy(true);
    try {
      const { ok, data } = await apiFetch(`${API}/api/workspaces/${ws.workspace_id}/content`, {
        method: 'DELETE', headers: headers(), body: JSON.stringify({ kind, id }),
      });
      if (!ok) throw new Error(data?.detail || 'Failed to remove');
      setContent(prev => prev.filter(c => !(c.kind === kind && c.id === id)));
      onChanged?.();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const { ok, data } = await apiFetch(`${API}/api/workspaces/${ws.workspace_id}/publish`, {
        method: 'POST', headers: headers(),
      });
      if (!ok) throw new Error(data?.detail || 'Publish failed');
      toast.success(`Published v${data.published_version}`);
      onChanged?.();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  const assetOptions = (allAssets?.[pickKind === 'model' ? 'models' : pickKind === 'dashboard' ? 'dashboards' : pickKind === 'visual' ? 'visuals' : 'reports']) || [];

  return (
    <Dialog open={!!ws} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="glass-card border-border/50 rounded-2xl sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="manage-app-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LayoutGrid className="w-4 h-4 text-primary" /> Manage {getTerm('apps', 'singular')} — {ws.name}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-2">
          {/* Content */}
          <section className="space-y-2">
            <h4 className="text-xs font-semibold flex items-center gap-1.5"><LayoutGrid className="w-3 h-3" /> Bundled content ({content.length})</h4>
            <div className="space-y-1 max-h-48 overflow-y-auto pr-1">
              {content.length === 0 && <div className="text-xs text-muted-foreground italic">No content yet — pick assets below.</div>}
              {content.map((c, i) => {
                const Icon = KIND_ICON[c.kind] || LayoutGrid;
                return (
                  <div key={`${c.kind}-${c.id}-${i}`} className="flex items-center gap-2 px-2 py-1 rounded bg-muted/20 text-xs">
                    <Icon className="w-3 h-3 text-primary/70" />
                    <span className="font-mono text-[10px] text-muted-foreground uppercase">{c.kind}</span>
                    <span className="flex-1 truncate" title={c.id} data-testid={`content-name-${c.id}`}>{nameFor(c.kind, c.id)}</span>
                    <button onClick={() => removeAsset(c.kind, c.id)} disabled={busy} className="text-muted-foreground hover:text-destructive" data-testid={`remove-content-${c.id}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-1.5">
              <Select value={pickKind} onValueChange={setPickKind}>
                <SelectTrigger className="h-8 text-xs rounded-lg bg-muted/30 border-border/30 w-32" data-testid="pick-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dashboard">Dashboard</SelectItem>
                  <SelectItem value="report">Report</SelectItem>
                  <SelectItem value="model">Model</SelectItem>
                  <SelectItem value="visual">Visual</SelectItem>
                </SelectContent>
              </Select>
              <Select value={pickId} onValueChange={setPickId}>
                <SelectTrigger className="h-8 text-xs rounded-lg bg-muted/30 border-border/30 flex-1" data-testid="pick-asset-select"><SelectValue placeholder={`Pick a ${pickKind}…`} /></SelectTrigger>
                <SelectContent>
                  {assetOptions.map(a => (
                    <SelectItem key={a.id} value={a.id} className="text-xs">{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={addAsset} disabled={!pickId || busy} className="rounded-lg h-8 text-xs" data-testid="add-content-btn">
                <Plus className="w-3 h-3 mr-1" /> Add
              </Button>
            </div>
          </section>

          {/* Audience */}
          <section className="space-y-2">
            <h4 className="text-xs font-semibold flex items-center gap-1.5"><Users className="w-3 h-3" /> Audience (blank = everyone in tenant)</h4>
            <div>
              <label className="text-[10px] text-muted-foreground">Specific users (pick from list or type an ID)</label>
              <MultiComboInput value={audienceUsers} onChange={setAudienceUsers} options={userOptions} placeholder="Add a user…" allowCustom testId="audience-users" />
            </div>
            <div>
              <label className="text-[10px] text-muted-foreground">Roles (pick from list or type a role)</label>
              <MultiComboInput value={audienceRoles} onChange={setAudienceRoles} options={roleOptions} placeholder="Add a role…" allowCustom testId="audience-roles" />
            </div>
            <Button size="sm" variant="outline" onClick={saveAudience} disabled={busy} className="rounded-lg h-7 text-xs" data-testid="save-audience-btn">
              Save audience
            </Button>
          </section>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} className="rounded-lg">Close</Button>
          <Button onClick={publish} disabled={busy || content.length === 0} className="rounded-lg" data-testid="publish-app-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1" />}
            {ws.status === 'published' ? `Publish v${(ws.published_version || 0) + 1}` : 'Publish'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AppsPage() {
  useTerms();
  const asList = pickArray;
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = ['admin', 'tenant_admin', 'super_admin'].includes(user?.role || '');
  const [published, setPublished] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allAssets, setAllAssets] = useState({ dashboards: [], reports: [], models: [], visuals: [] });
  const [sysUsers, setSysUsers] = useState([]);
  const [sysRoles, setSysRoles] = useState([]);
  const [manage, setManage] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [promoteTarget, setPromoteTarget] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pub, all] = await Promise.all([
        apiFetch(`${API}/api/workspaces/published`, { method: 'GET' }),
        isAdmin ? apiFetch(`${API}/api/workspaces`, { method: 'GET' }) : Promise.resolve({ ok: true, data: [] }),
      ]);
      setPublished(asList(pub, 'workspaces'));
      if (isAdmin) {
        const draftWs = asList(all, 'workspaces').filter(w => w.status !== 'published' && w.workspace_type === 'app');
        setDrafts(draftWs);
      }
    } catch (e) {
      toast.error(`Failed to load Apps: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { load(); }, [load]);

  // For admin asset picker — preload dashboards, reports, models, visuals
  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([
      apiFetch(`${API}/api/dashboards`, { method: 'GET' }),
      apiFetch(`${API}/api/reports`, { method: 'GET' }),
      apiFetch(`${API}/api/semantic/models`, { method: 'GET' }),
      apiFetch(`${API}/api/visuals`, { method: 'GET' }),
    ]).then(([d, r, m, v]) => {
      setAllAssets({
        dashboards: asList(d, 'dashboards').map(x => ({ id: x.dashboard_id, name: x.name })),
        reports:    asList(r, 'reports').map(x => ({ id: x.report_id, name: x.name })),
        models:     asList(m, 'models').map(x => ({ id: x.model_id, name: x.name })),
        visuals:    asList(v, 'visuals').map(x => ({ id: x.visual_id, name: x.name })),
      });
    }).catch(() => { /* asset picker preload is best-effort */ });
  }, [isAdmin]);

  // Preload tenant users + roles so the App audience can be picked from a list
  // (free-form entry still allowed). Best-effort — admins only.
  useEffect(() => {
    if (!isAdmin) return;
    Promise.all([
      apiFetch(`${API}/api/rbac/users`, { method: 'GET' }),
      apiFetch(`${API}/api/rbac/roles`, { method: 'GET' }),
    ]).then(([u, r]) => {
      setSysUsers(Array.isArray(u?.data) ? u.data : asList(u, 'users'));
      setSysRoles(Array.isArray(r?.data) ? r.data : asList(r, 'roles'));
    }).catch(() => { /* audience picker preload is best-effort */ });
  }, [isAdmin]);

  const handleCreate = async () => {
    const name = prompt('App name?');
    if (!name) return;
    try {
      const csrf = decodeURIComponent((document.cookie.match(/(?:^|; )csrf_token=([^;]+)/) || [])[1] || '');
      const { ok, data } = await apiFetch(`${API}/api/workspaces`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
        body: JSON.stringify({ name, description: '', workspace_type: 'app' }),
      });
      if (!ok) throw new Error(data?.detail || 'Failed to create');
      toast.success(`Created "${name}"`);
      setManage({ ...data, content: [], audience: {} });
      load();
    } catch (e) { toast.error(e.message); }
  };

  const openApp = (ws) => navigate(`/apps/${ws.workspace_id}`);

  const handleExport = async (ws) => {
    try {
      const { ok, data } = await apiFetch(`${API}/api/workspaces/${ws.workspace_id}/export`, { method: 'GET' });
      if (!ok) throw new Error(data?.detail || 'Export failed');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safe = (ws.name || 'app').replace(/[^a-z0-9]+/gi, '_').toLowerCase();
      a.href = url;
      a.download = `inzara-app-${safe}.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported "${ws.name}"`);
    } catch (e) { toast.error(e.message); }
  };

  const handleImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (file) e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setImporting(true);
    try {
      const text = await file.text();
      let payload;
      try { payload = JSON.parse(text); } catch { throw new Error('File is not valid JSON'); }
      if (!payload?.inzara_app_export) throw new Error('Not a valid INZARA App export file');
      const { ok, data } = await apiFetch(`${API}/api/workspaces/import`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!ok) throw new Error(data?.detail || 'Import failed');
      toast.success(`Imported "${data.name}" as a draft`);
      await load();
      setManage({ ...data, content: data.content || [], audience: data.audience || {} });
    } catch (err) { toast.error(err.message); } finally { setImporting(false); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="apps-page">
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        onChange={handleImportFile}
        className="hidden"
        data-testid="import-app-input"
      />
      <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-2">
            <LayoutGrid className="w-7 h-7 text-primary" /> {getTerm('apps')}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Curated bundles of dashboards, reports, models, and visuals — published to specific audiences with frozen versions.
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button onClick={() => fileInputRef.current?.click()} variant="outline" className="rounded-xl" disabled={importing} data-testid="import-app-btn">
              {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />} Import
            </Button>
            <Button onClick={handleCreate} className="rounded-xl" data-testid="new-app-btn">
              <Plus className="w-4 h-4 mr-2" /> New {getTerm('apps', 'singular')}
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-primary animate-spin" /></div>
      ) : (
        <div className="space-y-8">
          <section>
            <h2 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              Published {getTerm('apps')} <span className="text-muted-foreground/80">({published.length})</span>
            </h2>
            {published.length === 0 ? (
              <div className="glass-card rounded-2xl p-8 text-center text-sm text-muted-foreground">
                No published {getTerm('apps', 'plural', 'lower')} yet.{isAdmin && ' Create a draft and click Publish.'}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {published.map(ws => <AppCard key={ws.workspace_id} ws={ws} isAdmin={isAdmin} onOpen={openApp} onManage={setManage} onExport={handleExport} onDelete={setDeleteTarget} onPromote={setPromoteTarget} />)}
              </div>
            )}
          </section>

          {isAdmin && drafts.length > 0 && (
            <section data-testid="drafts-section">
              <h2 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
                Drafts <span className="text-muted-foreground/80">({drafts.length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {drafts.map(ws => <AppCard key={ws.workspace_id} ws={ws} isAdmin={isAdmin} onOpen={() => setManage(ws)} onManage={setManage} onExport={handleExport} onDelete={setDeleteTarget} onPromote={setPromoteTarget} />)}
              </div>
            </section>
          )}
        </div>
      )}

      <ManageDialog ws={manage} allAssets={allAssets} sysUsers={sysUsers} sysRoles={sysRoles} onClose={() => setManage(null)} onChanged={load} />
      <DeleteAppDialog ws={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirmed={load} />
      <AppPromoteDialog ws={promoteTarget} onClose={() => setPromoteTarget(null)} />
    </div>
  );
}
